import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerContent,
  Input,
  VStack,
  Text,
  Skeleton,
  Button,
  HStack,
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import SportNotificationSwitch from "./SportNotificationSwitch";
import {
  getSports,
  getSubscriptions,
  getUserDetails,
  setSubscriptions,
  Sport,
} from "../data/api";
import useAuth from "../hooks/useAuth";
import { NotificationContext } from "../context/notificationContext";

const NotificationSettings = () => {
  const { isOpen, onOpen, onClose } = useContext(NotificationContext);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState("");
  const [sports, setSports] = useState<Sport[] | undefined>();
  const { token, userDetails } = useAuth();
  const [error, setError] = useState(false);
  const [subscriptionsData, setSubscriptionsData] = useState<number[]>();

  useEffect(() => {
    if (isOpen) {
      getSubscriptions(token, userDetails.preferred_username)
        .then((data) => setSubscriptionsData(data))
        .catch(() => setError(true));
    }
    if (isOpen && sports === undefined && token) {
      getSports(token).then((sports) =>
        setSports(
          sports.sort((a, b) =>
            a.title > b.title ? 1 : a.title < b.title ? -1 : 0
          )
        )
      );
    }
  }, [isOpen, token, userDetails, sports]);

  useEffect(() => {
    if (
      subscriptionsData !== undefined &&
      token &&
      userDetails.preferred_username
    ) {
      setSubscriptions(token, userDetails.preferred_username, {
        sportIds: subscriptionsData,
      });
    }
  }, [subscriptionsData, token, userDetails.preferred_username]);

  const filteredSports = useMemo(() => {
    if (search && sports)
      return sports.filter((s) =>
        s.title.toLowerCase().includes(search.toLowerCase())
      );
    return sports;
  }, [search, sports]);

  return (
    <>
      <IconButton
        position="absolute"
        top={5}
        right={5}
        ref={btnRef}
        size="lg"
        title="Notification Settings"
        icon={<BellIcon />}
        onClick={onOpen}
        aria-label="Notification Settings"
        borderRadius="100%"
      ></IconButton>

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            Notification Settings
            <Input
              placeholder="Search for a sport"
              mt={5}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </DrawerHeader>
          <DrawerBody>
            <VStack mb={5}>
              {error ? (
                <Box>
                  <Text>Error fetching subscriptions</Text>
                </Box>
              ) : filteredSports && subscriptionsData ? (
                <>
                  <HStack mb={3}>
                    <Button
                      size="xs"
                      onClick={() =>
                        setSubscriptionsData(sports?.map((s) => s.id) ?? [])
                      }
                    >
                      Enable all alerts
                    </Button>
                    <Button size="xs" onClick={() => setSubscriptionsData([])}>
                      Disable all alerts
                    </Button>
                  </HStack>
                  {filteredSports.map((sport, i) => (
                    <SportNotificationSwitch
                      key={i}
                      sport={sport}
                      onToggle={() => {
                        if (subscriptionsData) {
                          if (subscriptionsData.includes(sport.id)) {
                            setSubscriptionsData(
                              subscriptionsData.filter((id) => id !== sport.id)
                            );
                          } else {
                            setSubscriptionsData([
                              ...subscriptionsData,
                              sport.id,
                            ]);
                          }
                        }
                      }}
                      checked={subscriptionsData?.includes(sport.id) ?? false}
                    />
                  ))}
                </>
              ) : (
                [...new Array(30)].map((e, i) => (
                  <Skeleton height={10} w="full" key={i} />
                ))
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default NotificationSettings;
